import React, { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Toll as TokenIcon } from '@mui/icons-material';
import {
  getPublicShareUuid,
  getShareTokens,
  setShareTokens,
  subscribeShareTokens,
  type ShareTokens,
} from '../services/shareContext';
import { consumeShareToken } from '../services/superPickerPublicApi';
import OutOfTokensDialog from './OutOfTokensDialog';

/**
 * Reusable confirmation dialog for any public-side action that costs a token.
 *
 * Flow:
 *   1. Caller mounts `<TokenSpendDialog open ...>` and supplies `onConfirmed`.
 *   2. User clicks the primary button. We POST
 *      `/api/super-picker-public/share/:uuid/consume`, push the returned
 *      counters into `shareContext` (so the header chip updates), then call
 *      `onConfirmed()` to run the original action.
 *   3. If the consume call fails (network error, `NO_TOKENS_LEFT`) we surface
 *      an inline alert and keep the dialog open.
 *
 * The companion `useTokenSpend` hook below provides a thin imperative wrapper
 * for callers that prefer "open dialog → await user confirm" semantics.
 */

/**
 * Result returned from a custom `spend` function. Mirrors the shape of
 * `consumeShareToken` so the dialog can always update the header chip
 * uniformly, with an optional `data` payload (e.g. the unmasked prices
 * returned by `revealSection`) handed back to `onConfirmed`.
 */
export interface TokenSpendResult<TData = unknown> {
  remainingTokens: number;
  maxTokens: number;
  data?: TData;
}

export interface TokenSpendDialogProps<TData = unknown> {
  open: boolean;
  onClose: () => void;
  /**
   * Called after the token has been successfully consumed. The dialog is
   * closed automatically before this fires. Receive the updated counters
   * and any `data` returned by the `spend` function (e.g. unmasked prices).
   */
  onConfirmed: (result: {
    remaining: number;
    max: number;
    data?: TData;
  }) => void | Promise<void>;
  /**
   * Optional custom spender. Defaults to `consumeShareToken(uuid)` — used by
   * generic "1 token to run an action" flows like the Project Research
   * Search button. Reveal flows pass `() => revealSection(uuid, body)` so a
   * single backend call both spends the token and returns unmasked data.
   *
   * The function MUST decrement the share's token counter server-side and
   * return the updated counters; the dialog will not call any other
   * endpoint. Throw an `Error` (or one with `.code = 'NO_TOKENS_LEFT'`) to
   * surface a message in the dialog.
   */
  spend?: () => Promise<TokenSpendResult<TData>>;
  /**
   * Short verb describing the action — used in the confirm button label
   * (e.g. `actionLabel="Search"` ⇒ "Search (1 token)"). Defaults to "Continue".
   */
  actionLabel?: string;
  /**
   * Headline shown at the top of the dialog. Defaults to "Confirm action".
   */
  title?: string;
  /**
   * Body copy explaining what the action costs. A sensible default is
   * generated from `actionLabel` and the current token count.
   */
  description?: React.ReactNode;
  /** Tokens charged on confirm. Defaults to `1`. */
  tokenCost?: number;
}

const useShareTokens = (): ShareTokens | null =>
  React.useSyncExternalStore(subscribeShareTokens, getShareTokens, () => null);

export function TokenSpendDialog<TData = unknown>({
  open,
  onClose,
  onConfirmed,
  spend,
  actionLabel = 'Continue',
  title = 'Confirm action',
  description,
  tokenCost = 1,
}: TokenSpendDialogProps<TData>): React.ReactElement {
  const tokens = useShareTokens();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset transient state every time the dialog is (re-)opened.
  React.useEffect(() => {
    if (open) {
      setBusy(false);
      setError(null);
    }
  }, [open]);

  const handleClose = () => {
    if (busy) return;
    onClose();
  };

  const handleConfirm = useCallback(async () => {
    const uuid = getPublicShareUuid();
    if (!uuid && !spend) {
      // No share recipient context AND no custom spender — fall through and
      // run the action anyway. Agent EXTERNAL sessions never need to spend
      // tokens; the dialog just acts as a confirmation prompt.
      onClose();
      await onConfirmed({ remaining: 0, max: 0 });
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // Default spender targets the generic `/consume` endpoint. Callers
      // that need both a token-spend AND a data fetch (reveal flows) pass
      // their own `spend` so the round-trip happens in one request.
      const spender: () => Promise<TokenSpendResult<TData>> =
        spend ??
        (async () => {
          if (!uuid) {
            // Unreachable in practice — guarded above — but keeps the
            // type system happy.
            throw new Error('Missing share context.');
          }
          const r = await consumeShareToken(uuid);
          return {
            remainingTokens: r.remainingTokens,
            maxTokens: r.maxTokens,
            data: undefined as TData | undefined,
          };
        });
      const next = await spender();
      setShareTokens({ remaining: next.remainingTokens, max: next.maxTokens });
      onClose();
      await onConfirmed({
        remaining: next.remainingTokens,
        max: next.maxTokens,
        data: next.data,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not spend token.');
    } finally {
      setBusy(false);
    }
  }, [onClose, onConfirmed, spend]);

  const hasNoneLeft = tokens ? tokens.remaining < tokenCost : false;

  const defaultDescription = (
    <>
      This will use <strong>{tokenCost} token{tokenCost === 1 ? '' : 's'}</strong>
      {tokens
        ? ` from your remaining ${tokens.remaining} of ${tokens.max}.`
        : '.'}{' '}
      Are you sure you want to continue?
    </>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <TokenIcon sx={{ color: 'var(--color-accent)' }} />
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
          {description ?? defaultDescription}
        </Typography>
        {tokens && (
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              bgcolor: 'rgba(var(--color-accent-rgb, 232, 160, 70), 0.12)',
              border: '1px solid rgba(var(--color-accent-rgb, 232, 160, 70), 0.4)',
            }}
          >
            <TokenIcon
              sx={{ color: 'var(--color-accent)', fontSize: '1.1rem' }}
            />
            <Typography
              sx={{ fontSize: '0.9rem', fontWeight: 600, lineHeight: 1 }}
            >
              Remaining:{' '}
              <Box
                component="span"
                sx={{ color: 'var(--color-accent)', fontWeight: 700 }}
              >
                {tokens.remaining}
              </Box>
              <Box component="span" sx={{ opacity: 0.6, fontWeight: 500 }}>
                {' / '}
                {tokens.max}
              </Box>
            </Typography>
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
        {!error && hasNoneLeft && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            You have no tokens left. Please contact your agent for more.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={busy} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={busy || hasNoneLeft}
          variant="contained"
          startIcon={
            busy ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <TokenIcon />
            )
          }
          sx={{
            bgcolor: 'var(--color-accent)',
            '&:hover': { bgcolor: 'var(--color-accent)' },
            fontWeight: 700,
          }}
        >
          {busy ? 'Spending…' : `${actionLabel} (${tokenCost} token${tokenCost === 1 ? '' : 's'})`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Imperative companion to `TokenSpendDialog`. Returns a `request` function
 * that opens the dialog and resolves when the user either confirms (after
 * the token is spent and `onConfirmed` runs) or dismisses it. Render the
 * `dialog` node returned alongside any UI that needs the prompt.
 *
 * Example:
 *
 *   const tokenSpend = useTokenSpend({
 *     title: 'Confirm search',
 *     actionLabel: 'Search',
 *     onConfirmed: () => runSearch(),
 *   });
 *   ...
 *   <Button onClick={tokenSpend.request}>Search</Button>
 *   {tokenSpend.dialog}
 */
export function useTokenSpend<TData = unknown>(options: {
  title?: string;
  actionLabel?: string;
  description?: React.ReactNode;
  /**
   * Custom token-spend implementation. See `TokenSpendDialogProps.spend` for
   * the contract. Omit for the default `consumeShareToken` flow (Search).
   */
  spend?: () => Promise<TokenSpendResult<TData>>;
  /** Tokens charged on confirm. Defaults to `1`. */
  tokenCost?: number;
  onConfirmed: (result: {
    remaining: number;
    max: number;
    data?: TData;
  }) => void | Promise<void>;
}): { request: () => void; dialog: React.ReactNode } {
  const [open, setOpen] = useState(false);
  const tokens = useShareTokens();
  const { onConfirmed, title, actionLabel, description, spend, tokenCost } = options;
  // Snapshot the dialog variant at `request()` time so we don't flip mid-
  // spend. After a successful spend that exhausts the wallet the variant is
  // promoted to `'out'` inside `wrappedOnConfirmed` below so the recipient
  // immediately sees the contact-your-agent prompt.
  const [variant, setVariant] = useState<'spend' | 'out'>('spend');
  const cost = tokenCost ?? 1;
  const request = useCallback(() => {
    setVariant(tokens && tokens.remaining < cost ? 'out' : 'spend');
    setOpen(true);
  }, [tokens, cost]);
  // Intercept the caller's `onConfirmed` so that the moment the last token
  // is spent we swap the closed `TokenSpendDialog` for an open
  // `OutOfTokensDialog`. Previously the dialog simply closed and the user
  // saw a one-frame flash of the out-of-tokens dialog (a stale render path
  // where `isOutOfTokens` flipped true before `open` flipped false). Now we
  // explicitly drive the transition: set variant='out' and re-open in the
  // same task so React batches it into a single commit.
  const wrappedOnConfirmed = useCallback(async (result: {
    remaining: number;
    max: number;
    data?: TData;
  }) => {
    await onConfirmed(result);
    if (result.remaining <= 0) {
      setVariant('out');
      setOpen(true);
    }
  }, [onConfirmed]);
  return {
    request,
    dialog: variant === 'out' ? (
      <OutOfTokensDialog
        open={open}
        onClose={() => {
          setOpen(false);
          // Reset to 'spend' so the next `request()` re-evaluates the
          // wallet — important if the agent tops up tokens while the
          // dialog is dismissed.
          setVariant('spend');
        }}
      />
    ) : (
      <TokenSpendDialog<TData>
        open={open}
        onClose={() => setOpen(false)}
        onConfirmed={wrappedOnConfirmed}
        spend={spend}
        title={title}
        actionLabel={actionLabel}
        description={description}
        tokenCost={cost}
      />
    ),
  };
}

export default TokenSpendDialog;
